import React, { useEffect, useState, useRef } from "react";
import classes from './AssetRevaluation.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText, Calendar, DollarSign, TrendingUp, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetRevaluation = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_revaluations: 0,
        total_surplus: 0,
        total_deficit: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [assetRevaluations, setAssetRevaluations] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedRevaluation, setSelectedRevaluation] = useState(null);

    // Create Modal state
    const [assets, setAssets] = useState([]);
    const [createFormData, setCreateFormData] = useState({
        asset_id: "",
        revaluation_date: "",
        old_value: "",
        new_value: "",
        surplus_or_deficit: "",
        journal_entry_id: "",
        company_id: ""
    });
    const [createLoading, setCreateLoading] = useState({
        assets: false
    });
    const [submitting, setSubmitting] = useState(false);

    // For searchable selects in create modal
    const [searchTerms, setSearchTerms] = useState({
        asset: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        asset: false
    });

    // Create refs for each dropdown
    const assetRef = useRef(null);

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        id: "",
        asset_name: "",
        asset_code: "",
        revaluation_date: "",
        old_value: "",
        new_value: "",
        surplus_or_deficit: "",
        revaluation_type: "",
        journal_entry_id: "",
        company_id: ""
    });

    // Helper function to get company ID from any source
    const getCompanyIdFromStorage = () => {
        // First check direct company_id
        const directCompanyId = localStorage.getItem("company_id");
        if (directCompanyId) return directCompanyId;

        // Check companyInfo
        const companyInfo = localStorage.getItem("companyInfo");
        if (companyInfo) {
            try {
                const parsed = JSON.parse(companyInfo);
                const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                if (id) return String(id);
            } catch (error) {
                console.error("Error parsing companyInfo:", error);
            }
        }

        // Check user data
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                if (parsed.company_id) return String(parsed.company_id);
                if (parsed.company) {
                    const id = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                    if (id) return String(id);
                }
                if (parsed.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0) {
                    const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1);
                    if (mainCompany) {
                        const id = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                        if (id) return String(id);
                    }
                    const firstCompany = parsed.companies[0];
                    const id = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                    if (id) return String(id);
                }
            } catch (error) {
                console.error("Error parsing user data:", error);
            }
        }

        return null;
    };

    // Get company ID from localStorage
    useEffect(() => {
        const id = getCompanyIdFromStorage();
        if (id) {
            console.log("Found company ID:", id);
            setCompanyId(id);
            setCreateFormData(prev => ({
                ...prev,
                company_id: id
            }));
        } else {
            console.warn("No company ID found in any source");
        }
    }, []);

    // Calculate surplus/deficit when old_value or new_value changes
    useEffect(() => {
        if (createFormData.old_value && createFormData.new_value) {
            const oldVal = parseFloat(createFormData.old_value) || 0;
            const newVal = parseFloat(createFormData.new_value) || 0;
            const surplusDeficit = newVal - oldVal;

            setCreateFormData(prev => ({
                ...prev,
                surplus_or_deficit: surplusDeficit.toString()
            }));
        }
    }, [createFormData.old_value, createFormData.new_value]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assetRef.current && !assetRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, asset: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch data for create modal when opened
    useEffect(() => {
        if (showCreateModal) {
            const validCompanyId = getCompanyIdFromStorage();
            if (validCompanyId) {
                if (!companyId) setCompanyId(validCompanyId);
                fetchAssets(validCompanyId);
            } else {
                console.warn("Cannot fetch assets: Company ID not available");
                Swal.fire({
                    icon: 'warning',
                    title: 'Company ID Missing',
                    text: 'Please select a company and try again.',
                    confirmButtonText: 'Go to Dashboard'
                }).then(() => {
                    setShowCreateModal(false);
                    navigate('/dashboard');
                });
            }
        }
    }, [showCreateModal]);

    // Fetch asset revaluations when companyId is available
    useEffect(() => {
        if (companyId) {
            console.log("Company ID available, fetching asset revaluations for company:", companyId);
            fetchAssetRevaluations();
        } else {
            console.warn("Company ID not available yet, waiting...");
        }
    }, [companyId]);

    const fetchAssetRevaluations = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = companyId || getCompanyIdFromStorage();

            if (!token) {
                console.error("No auth token found");
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            if (!validCompanyId) {
                console.error("Company ID is required");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please select a company and try again.'
                });
                setLoading(false);
                return;
            }

            console.log(`Fetching asset revaluations for company ID: ${validCompanyId}`);

            const response = await axios.get(`${BASE_URL}/v1/assetrevaluation`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: validCompanyId
                }
            });

            console.log("Asset revaluations response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetRevaluations(response.data.data);

                // Update metrics
                let totalSurplus = 0;
                let totalDeficit = 0;

                response.data.data.forEach(rev => {
                    const value = parseFloat(rev.surplus_or_deficit) || 0;
                    if (value > 0) {
                        totalSurplus += value;
                    } else {
                        totalDeficit += Math.abs(value);
                    }
                });

                setSelectedMetrics({
                    total_revaluations: response.data.data.length,
                    total_surplus: totalSurplus,
                    total_deficit: totalDeficit
                });

                console.log(`Loaded ${response.data.data.length} asset revaluations`);
            } else {
                console.warn("Unexpected response format:", response.data);
                setAssetRevaluations([]);
            }
        } catch (error) {
            console.error("Error fetching asset revaluations:", error);

            if (error.response) {
                console.error("Error response data:", error.response.data);
                console.error("Error response status:", error.response.status);

                let errorMessage = "Failed to fetch asset revaluations";
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (error.response.status === 404) {
                    errorMessage = "API endpoint not found";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: errorMessage
                });
            } else if (error.request) {
                console.error("No response received:", error.request);
                Swal.fire({
                    icon: 'error',
                    title: 'Network Error',
                    text: 'No response from server. Please check your connection.'
                });
            } else {
                console.error("Error setting up request:", error.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An error occurred while setting up the request'
                });
            }

            setAssetRevaluations([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssets = async (companyIdParam = null) => {
        setCreateLoading(prev => ({ ...prev, assets: true }));
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = companyIdParam || companyId || getCompanyIdFromStorage();

            if (!validCompanyId) {
                console.error("Cannot fetch assets: No company ID");
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/fixedassets/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });

            console.log("Assets response:", response.data);

            if (response.data.status && Array.isArray(response.data.data)) {
                // Show all assets for revaluation (both active and fully depreciated can be revalued)
                setAssets(response.data.data);
                console.log(`Loaded ${response.data.data.length} assets for revaluation`);
            } else {
                console.warn("Unexpected assets response format:", response.data);
                setAssets([]);
            }
        } catch (error) {
            console.error("Error fetching assets:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch assets. Please try again.'
            });
        } finally {
            setCreateLoading(prev => ({ ...prev, assets: false }));
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    // Process revaluation data for modal
    const processRevaluationData = (revaluation) => {
        console.log("Processing revaluation data:", revaluation);
        const surplusValue = parseFloat(revaluation.surplus_or_deficit) || 0;
        const revaluationType = surplusValue > 0 ? "Surplus" : surplusValue < 0 ? "Deficit" : "No Change";

        setModalData({
            id: revaluation.id,
            asset_name: revaluation.fixedasset?.asset_name || "N/A",
            asset_code: revaluation.fixedasset?.asset_code || "N/A",
            revaluation_date: revaluation.revaluation_date || "",
            old_value: revaluation.old_value || "",
            new_value: revaluation.new_value || "",
            surplus_or_deficit: revaluation.surplus_or_deficit || "",
            revaluation_type: revaluationType,
            journal_entry_id: revaluation.journal_entry_id || "",
            company_id: revaluation.company_id || ""
        });
    };

    // Handle View/Edit icon clicks
    const handleViewClick = async (revaluation) => {
        setModalMode("view");
        setSelectedRevaluation(revaluation);
        setModalLoading(true);
        setShowViewEditModal(true);
        processRevaluationData(revaluation);
        setModalLoading(false);
    };

    const handleEditClick = async (revaluation) => {
        setModalMode("edit");
        setSelectedRevaluation(revaluation);
        setModalLoading(true);
        setShowViewEditModal(true);
        processRevaluationData(revaluation);
        setModalLoading(false);
    };

    // Handle modal input changes for view/edit
    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));

        // Recalculate surplus/deficit if old_value or new_value changes
        if (field === 'old_value' || field === 'new_value') {
            const oldVal = field === 'old_value' ? parseFloat(value) || 0 : parseFloat(modalData.old_value) || 0;
            const newVal = field === 'new_value' ? parseFloat(value) || 0 : parseFloat(modalData.new_value) || 0;
            const surplusDeficit = newVal - oldVal;

            setModalData(prev => ({
                ...prev,
                surplus_or_deficit: surplusDeficit.toString(),
                revaluation_type: surplusDeficit > 0 ? "Surplus" : surplusDeficit < 0 ? "Deficit" : "No Change"
            }));
        }
    };

    // Handle create form changes
    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle searchable select for create modal
    const handleSearchChange = (field, e) => {
        setSearchTerms(prev => ({ ...prev, [field]: e.target.value }));
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
    };

    const handleSelect = (field, item) => {
        setCreateFormData(prev => ({
            ...prev,
            asset_id: item.id
        }));

        const displayName = `${item.asset_name} (${item.asset_code})`;
        setSearchTerms(prev => ({ ...prev, asset: displayName }));
        setShowDropdowns(prev => ({ ...prev, asset: false }));
    };

    const getSelectedName = (field) => {
        const id = createFormData.asset_id;
        if (!id) return "";

        const item = assets.find(i => i.id === id);
        if (!item) return "";

        return `${item.asset_name} (${item.asset_code})`;
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms.asset || "").toLowerCase();

        if (!Array.isArray(assets)) {
            return [];
        }

        return assets.filter(item => {
            if (!item) return false;

            const assetName = (item.asset_name || "").toLowerCase();
            const assetCode = (item.asset_code || "").toLowerCase();
            return assetName.includes(searchTerm) || assetCode.includes(searchTerm);
        });
    };

    // FIXED: Handle save for edit mode with correct endpoint
    // FIXED: Handle save for edit mode with all required fields
    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");

                // Double-check company ID
                let currentCompanyId = modalData.company_id || companyId || getCompanyIdFromStorage();

                if (!currentCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please select a company and try again.'
                    });
                    setSaveLoading(false);
                    return;
                }

                // Find the asset ID from the selected revaluation
                // The asset_id is needed for validation
                const assetId = selectedRevaluation?.asset_id;

                if (!assetId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Asset ID not found for this revaluation record.'
                    });
                    setSaveLoading(false);
                    return;
                }

                // Prepare request data with ALL required fields
                const requestData = {
                    id: selectedRevaluation.id,  // Required for edit endpoint
                    asset_id: assetId,           // Required by validation
                    revaluation_date: modalData.revaluation_date,
                    old_value: modalData.old_value,
                    new_value: modalData.new_value,
                    surplus_or_deficit: modalData.surplus_or_deficit,
                    journal_entry_id: modalData.journal_entry_id || null,
                    company_id: currentCompanyId
                };

                console.log("Updating asset revaluation with data:", requestData);

                // Use the correct edit endpoint
                const response = await axios.post(
                    `${BASE_URL}/v1/assetrevaluation/edit`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                console.log("Update response:", response.data);

                if (response.data.status) {
                    await fetchAssetRevaluations();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Asset revaluation updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update asset revaluation";
                    if (response.data.message) {
                        if (typeof response.data.message === 'object') {
                            errorMessage = Object.values(response.data.message).flat().join(', ');
                        } else {
                            errorMessage = response.data.message;
                        }
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating asset revaluation:", error);

                let errorMessage = "Failed to update asset revaluation";

                if (error.response) {
                    if (error.response.data && error.response.data.message) {
                        if (typeof error.response.data.message === 'object') {
                            errorMessage = Object.values(error.response.data.message).flat().join(', ');
                        } else {
                            errorMessage = error.response.data.message;
                        }
                    } else if (error.response.status === 422) {
                        errorMessage = "Validation error. Please check all required fields.";
                    } else if (error.response.status === 404) {
                        errorMessage = "Asset revaluation record not found.";
                    }
                } else if (error.request) {
                    errorMessage = "No response from server. Please check your connection.";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    // Handle create submit
    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.asset_id ||
            !createFormData.revaluation_date ||
            !createFormData.old_value ||
            !createFormData.new_value) {
            Swal.fire("Error", "Please fill in all required fields", "error");
            return;
        }

        // Double-check company ID
        let currentCompanyId = companyId || getCompanyIdFromStorage();

        if (!currentCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please select a company and try again.',
                confirmButtonText: 'Go to Dashboard'
            }).then(() => {
                navigate('/dashboard');
            });
            return;
        }

        if (!companyId) {
            setCompanyId(currentCompanyId);
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                asset_id: createFormData.asset_id,
                revaluation_date: createFormData.revaluation_date,
                old_value: createFormData.old_value,
                new_value: createFormData.new_value,
                surplus_or_deficit: createFormData.surplus_or_deficit,
                journal_entry_id: createFormData.journal_entry_id || null,
                company_id: currentCompanyId
            };

            console.log("Creating asset revaluation with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/v1/assetrevaluation/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("Create response:", response.data);

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Asset revaluation recorded successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });

                resetCreateForm();
                onClose();
                await refreshRevaluations();
            } else {
                Swal.fire("Error", response.data?.message || "Failed to record revaluation", "error");
            }
        } catch (error) {
            console.error("Error creating asset revaluation:", error);

            let errorMessage = "Something went wrong while recording revaluation";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }

            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    // FIXED: Handle delete revaluation with correct endpoint
    // FIXED: Handle delete revaluation with POST method
    const handleDeleteClick = async (revaluation) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                const validCompanyId = companyId || getCompanyIdFromStorage();

                if (!validCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Company ID not found.'
                    });
                    return;
                }

                console.log(`Deleting asset revaluation with ID: ${revaluation.id}, Company ID: ${validCompanyId}`);

                // FIXED: Use POST method instead of DELETE
                const response = await axios.post(
                    `${BASE_URL}/v1/assetrevaluation/delete`,
                    {
                        id: revaluation.id,
                        company_id: validCompanyId
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                console.log("Delete response:", response.data);

                if (response.data.status) {
                    await fetchAssetRevaluations();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Asset revaluation deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete asset revaluation");
                }
            } catch (error) {
                console.error("Error deleting asset revaluation:", error);

                let errorMessage = "Failed to delete asset revaluation";
                if (error.response && error.response.data && error.response.data.message) {
                    if (typeof error.response.data.message === 'object') {
                        errorMessage = Object.values(error.response.data.message).flat().join(', ');
                    } else {
                        errorMessage = error.response.data.message;
                    }
                } else if (error.response && error.response.status === 404) {
                    errorMessage = "Asset revaluation record not found";
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const resetCreateForm = () => {
        const validCompanyId = companyId || getCompanyIdFromStorage();
        setCreateFormData({
            asset_id: "",
            revaluation_date: "",
            old_value: "",
            new_value: "",
            surplus_or_deficit: "",
            journal_entry_id: "",
            company_id: validCompanyId || ""
        });
        setSearchTerms({
            asset: ""
        });
    };

    const onClose = () => {
        setShowCreateModal(false);
        resetCreateForm();
        setShowDropdowns({
            asset: false
        });
    };

    const refreshRevaluations = async () => {
        await fetchAssetRevaluations();
    };

    // Format currency
    const formatCurrency = (value) => {
        if (!value) return "0.00";
        return parseFloat(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString();
    };

    // Skeleton loader for table rows
    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    // Skeleton for modal form inputs
    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/transactions/sales-invoice/sale')}>
                            Sales Invoice
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/Inventory/PurchaseOrder')}>
                            Purchase Order
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Stock Delivery
                        </button>
                        <button onClick={() => navigate('/settings/product-services')} className={`${classes.btns} ${classes.greens}`}>
                            Product & Services
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Revaluation</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Revaluations</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_revaluations?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Revaluation entries</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Surplus</p>
                                    <p className={classes.kdptg2}>
                                        {formatCurrency(selectedMetrics?.total_surplus)}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Value increases</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.fiftkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Deficit</p>
                                    <p className={classes.kdptg3}>
                                        {formatCurrency(selectedMetrics?.total_deficit)}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Value decreases</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search revaluation" />
                            </div>
                            <div className={classes.selectWrapper}>
                                <select className={classes.dropdown}>
                                    <option value="">All Assets</option>
                                    {assets.map(asset => (
                                        <option key={asset.id} value={asset.id}>{asset.asset_name}</option>
                                    ))}
                                </select>
                                <span className={classes.dropdownArrow}>▼</span>
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Record Revaluation
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Asset</th>
                                <th>Revaluation Date</th>
                                <th>Old Value</th>
                                <th>New Value</th>
                                <th>Surplus/Deficit</th>
                                <th>Type</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : assetRevaluations.length > 0 ? (
                                assetRevaluations.map((revaluation, index) => {
                                    const surplusValue = parseFloat(revaluation.surplus_or_deficit) || 0;
                                    const isSurplus = surplusValue > 0;
                                    const isDeficit = surplusValue < 0;

                                    return (
                                        <tr key={revaluation.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                {revaluation.fixedasset?.asset_name || "—"}
                                                {revaluation.fixedasset?.asset_code && <div className={classes.smallText}>{revaluation.fixedasset.asset_code}</div>}
                                            </td>
                                            <td>{formatDate(revaluation.revaluation_date)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(revaluation.old_value)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatCurrency(revaluation.new_value)}</td>
                                            <td style={{
                                                textAlign: 'right',
                                                color: isSurplus ? '#28a745' : isDeficit ? '#dc3545' : 'inherit',
                                                fontWeight: '500'
                                            }}>
                                                {isSurplus ? '+' : ''}{formatCurrency(revaluation.surplus_or_deficit)}
                                            </td>
                                            <td>
                                                <span className={isSurplus ? classes.surplusBadge : isDeficit ? classes.deficitBadge : classes.neutralBadge}>
                                                    {isSurplus ? 'Surplus' : isDeficit ? 'Deficit' : 'No Change'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                    <Eye
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#007bff" }}
                                                        onClick={() => handleViewClick(revaluation)}
                                                    />
                                                    <Pencil
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#28a745" }}
                                                        onClick={() => handleEditClick(revaluation)}
                                                    />
                                                    <Trash2
                                                        size={16}
                                                        style={{ cursor: "pointer", color: "#dc3545" }}
                                                        onClick={() => handleDeleteClick(revaluation)}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                                        {loading ? "Loading..." : "No asset revaluation records found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <TrendingUp className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Revaluation Record" : "View Revaluation Record"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 6 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Asset</label>
                                        <input
                                            type="text"
                                            value={`${modalData.asset_name} (${modalData.asset_code})`}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Revaluation Date *</label>
                                        <input
                                            type={modalMode === "edit" ? "date" : "text"}
                                            value={modalData.revaluation_date}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("revaluation_date", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                            required={modalMode === "edit"}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Old Value *</label>
                                        <input
                                            type="number"
                                            value={modalData.old_value}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("old_value", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="0"
                                            step="0.01"
                                            style={{ textAlign: 'right' }}
                                            required={modalMode === "edit"}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>New Value *</label>
                                        <input
                                            type="number"
                                            value={modalData.new_value}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("new_value", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="0"
                                            step="0.01"
                                            style={{ textAlign: 'right' }}
                                            required={modalMode === "edit"}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Surplus/Deficit</label>
                                        <input
                                            type="number"
                                            value={modalData.surplus_or_deficit}
                                            readOnly
                                            className={`${classes.number2} ${parseFloat(modalData.surplus_or_deficit) > 0 ? classes.surplus : parseFloat(modalData.surplus_or_deficit) < 0 ? classes.deficit : ''}`}
                                            step="0.01"
                                            style={{ textAlign: 'right' }}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Revaluation Type</label>
                                        <input
                                            type="text"
                                            value={modalData.revaluation_type}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <input type="hidden" value={modalData.company_id} />
                                </>
                            )}
                        </div>

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Update Record" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <TrendingUp className={classes.icon} />
                                <h2>Record Asset Revaluation</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroup}>
                                    <label>Select Asset *</label>
                                    <div className={classes.searchSelectWrapper} ref={assetRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select asset..."
                                                value={showDropdowns.asset ? searchTerms.asset : getSelectedName('asset')}
                                                onChange={(e) => handleSearchChange('asset', e)}
                                                onClick={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                onFocus={() => setShowDropdowns(prev => ({ ...prev, asset: true }))}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.asset && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.assets ? (
                                                        <div className={classes.dropdownItem}>Loading assets...</div>
                                                    ) : filterItems('asset').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No assets found</div>
                                                    ) : (
                                                        filterItems('asset').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.asset_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('asset', item)}
                                                            >
                                                                {item.asset_name} ({item.asset_code})
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Revaluation Date *</label>
                                    <input
                                        type="date"
                                        name="revaluation_date"
                                        value={createFormData.revaluation_date}
                                        onChange={handleCreateChange}
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Old Value *</label>
                                    <input
                                        type="number"
                                        name="old_value"
                                        placeholder="0.00"
                                        value={createFormData.old_value}
                                        onChange={handleCreateChange}
                                        min="0"
                                        step="0.01"
                                        required
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>New Value *</label>
                                    <input
                                        type="number"
                                        name="new_value"
                                        placeholder="0.00"
                                        value={createFormData.new_value}
                                        onChange={handleCreateChange}
                                        min="0"
                                        step="0.01"
                                        required
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Surplus/Deficit</label>
                                    <input
                                        type="number"
                                        name="surplus_or_deficit"
                                        value={createFormData.surplus_or_deficit}
                                        readOnly
                                        className={`${classes.number2} ${parseFloat(createFormData.surplus_or_deficit) > 0 ? classes.surplus : parseFloat(createFormData.surplus_or_deficit) < 0 ? classes.deficit : ''}`}
                                        step="0.01"
                                        style={{ textAlign: 'right' }}
                                    />
                                    <small className={classes.helperText}>Auto-calculated (New Value - Old Value)</small>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={classes.saveBtn}
                                disabled={submitting}
                            >
                                {submitting ? "Recording..." : "Record Revaluation"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetRevaluation;